<template>
    <div class="relative bg-black" :class="isDark ? 'dark' : 'light'">

        <div v-if="connecting"
            class="absolute inset-0 z-50 flex flex-row gap-2 items-center justify-center bg-black bg-opacity-25 backdrop-blur-sm  text-white">
            <spinner />
            <span class="text-sm">Connecting</span>
        </div>

        <div class="bg-black bg-opacity-25 backdrop-blur-sm z-30 pointer-events-none absolute inset-0"
            :class="accountMenuShow ? '' : 'hidden'" />

        <div class="h-screen relative grid gridlayout mx-auto"
            :class="accountMenuShow ? 'overflow-hidden pointer-events-none' : ''">

            <header-menu :connected="connected" :authenticated="authenticated" :isDark="isDark" :sound="sound"
                :sounds="sounds" :notifications="notifications" :account="account" :isTeamWallet="isTeamWallet"
                :tokens="tokens" :tokensBalance="tokensBalance" :ticketsBalance="ticketsBalance" />

            <main class="container mx-auto overflow-auto relative">

                <games-list v-if="gamesList.show" :gamesList="gamesList" :token="token" :connected="connected"
                    :authenticated="authenticated" :subdomain="subdomain" :debug="debug" :jackpot="jackpot"
                    :bgLobby="bgLobby" :tokens="tokens" :ticketsBalance="ticketsBalance" @selected-tab="selectedTab"
                    :isTeamWallet="isTeamWallet" />

                <game-lobby v-if="gameLobby.show" :game="gameLobby.game" :queue="gamesList.queue" :private_queue="gamesList.private_queue" :spectate_queue="gamesList.spectate_queue" :token="token"
                    :connected="connected" :subdomain="subdomain" :jackpot="jackpot" :bgLobby="bgLobby" :tokens="tokens"
                    :ticketsBalance="ticketsBalance" :start_game="auto_start" :isTeamWallet="isTeamWallet" />

                <chat-box v-if="globalChat.show" :messages="globalChat.messages" :token="token" :account="account"
                    :connected="connected" :bgLobby="bgLobby" />

                <your-games-list v-if="yourGamesList.show" :token="token" :games="yourGamesList.games"
                    :fetching="yourGamesList.fetching" :next_page="yourGamesList.next_page"
                    :last_page="yourGamesList.last_page" :jackpot="jackpot" :bgLobby="bgLobby"
                    :tokens="tokens" :isTeamWallet="isTeamWallet" :show_all="yourGamesList.showAll" />

                <notifications v-if="notifications.show" :notifications="notifications" :bgLobby="bgLobby" />

                <transactions v-if="transactions.show" :token="token" :bgLobby="bgLobby" :gamesList="gamesList" />

                <revenue-share v-if="revenue.show" :token="token" :bgLobby="bgLobby" />

                <game v-if="game.show" :isDark="isDark" :sounds="sounds" :token="token" :account="account"
                    :game_id="game.id" :game_template_id="game.template_id" :winner="game.winner"
                    :game_templates="gamesList.list" :playersUpdate="game.players" :turns="game.turns"
                    :spritesList="sprites.list" :spritesFiles="sprites.files" :messages="game.messages" :grid="game.grid"
                    :countdown="game.countdown" :log="game.log" :bg="bgGame" :debug="debug" />

                <app-info v-if="appInfo.show" :bgLobby="bgLobby" />

                <game-config v-if="gameConfig.show" :isDark="isDark" :account="account" :gamesList="gamesList"
                    :token="token" :spritesList="sprites.list" :spritesFiles="sprites.files" />

                <admin-panel v-if="isTeamWallet && admin.show" :token="token" :tokens="tokens" :bgLobby="bgLobby"
                    :games="gamesList.list" />

            </main>

            <footer-menu :state="buttons" :newMessages="globalChat.new" :newGames="yourGamesList.new"
                :authenticated="authenticated" :game="game" :isTeamWallet="isTeamWallet" />

        </div>

        <account-menu v-if="accountMenuShow" :accountMenuShow="accountMenuShow" :isDark="isDark"
            :authenticated="authenticated" :connected="connected" :account="account" :online="online" :version="version"
            :isTeamWallet="isTeamWallet" />

        <change-name-modal v-if="modals.name_modal" :account="account" :token="token" />

        <change-pfp-modal v-if="modals.pfp_modal" :account="account" :token="token" />

        <deposit-modal v-if="modals.deposit_modal" :account="account" :token="token" />

        <withdraw-modal v-if="modals.withdraw_modal" :account="account" :token="token" />

        <swap-tokens-modal v-if="modals.swap_tokens_modal" :token="token" />

        <spin-modal v-if="modals.spin_modal" :token="token" :spin="spin" @spin-ready="spin.ready = true"
            @spin-reset="spin.ready = false; spin.prize = ''" />

        <start-game-modal v-if="modals.start_game_modal" />

        <div class="absolute inset-0 bg-black bg-opacity-25 backdrop-blur-sm flex items-center justify-center z-40"
            v-if="disclaimer">
            <div
                class="flex flex-col gap-4 items-center justify-center p-4 border border-gray-600 bg-lobby-bg rounded-md text-white drop-shadow-lg w-10/12">
                <span class="uppercase font-semibold">Terms of Service</span>
                <span class="text-sm text-center">I confirm that gambling is not forbidden in my jurisdiction and that I am
                    at least 18 years old.</span>
                <hr>
                <button @click.prevent="acceptedDisclaimer" type="button" class="z-50 modal-action-button">Confirm</button>
            </div>
        </div>

    </div>
</template>

<script>
import * as dayjs from 'dayjs'
import axios from 'axios'
import { Howl, Howler } from 'howler';

import { SolflareWalletAdapter } from '@solana/wallet-adapter-wallets'
import { BackpackWalletAdapter } from '@solana/wallet-adapter-backpack'
import { initWallet, useWallet } from 'solana-wallets-vue'
const wallets = [
    new SolflareWalletAdapter(),
    new BackpackWalletAdapter()
]
initWallet({ wallets, autoConnect: true })

// Menus
import HeaderMenu from './components/HeaderMenu.vue'
import FooterMenu from './components/FooterMenu.vue'
import AccountMenu from './components/AccountMenu.vue'
import Notifications from './components/Notifications.vue'
import Transactions from './components/Transactions.vue'
import RevenueShare from './components/RevenueShare.vue'
import GamesList from './components/GamesList.vue'
import GameLobby from './components/GameLobby.vue'
import GameConfig from './components/GameConfig.vue'
import YourGamesList from './components/YourGamesList.vue'
import Game from './components/Game.vue'
import ChatBox from './components/ChatBox.vue'
import AppInfo from './components/AppInfo.vue'
import AdminPanel from './components/Admin/AdminPanel.vue'
// Modals
import ChangeNameModal from './components/Modals/ChangeNameModal.vue'
import ChangePfpModal from './components/Modals/ChangePfpModal.vue'
import DepositModal from './components/Modals/DepositModal.vue'
import WithdrawModal from './components/Modals/WithdrawModal.vue'
import StartGameModal from './components/Modals/StartGameModal.vue'
import SwapTokensModal from './components/Modals/SwapTokensModal.vue'
import SpinModal from './components/Modals/SpinModal.vue'
// Bus
import bus from './mixins/bus'
// Stuff
import preloadImage from './mixins/preloadImage'

export default {
    components: {
        HeaderMenu,
        FooterMenu,
        AccountMenu,
        Notifications,
        Transactions,
        RevenueShare,
        GamesList,
        GameLobby,
        GameConfig,
        ChangeNameModal,
        ChangePfpModal,
        DepositModal,
        WithdrawModal,
        StartGameModal,
        YourGamesList,
        Game,
        ChatBox,
        AppInfo,
        AdminPanel,
        SwapTokensModal,
        SpinModal,
    },
    mixins: [bus],
    props: ['subdomain'],
    data() {
        // let isDark = localStorage.getItem('darkMode') === 'true'
        let sound = localStorage.getItem('sound') === 'true'
        let isDark = true
        let connected = useWallet().connected

        return {
            disclaimer: false,
            team: [
                '389i5QTdFP32bCL1c7VbXrDiFvpukDtm3aDrDVFeZFZJ', // Barlow
                '2efiMadpmG7JBEGvjrCrngkrPWFfgWmkUNTo9ZQ1HAaZ', // Barlow
                '4FHwMVw2TgbyNUCk62Qu6bPySEjLr5hJuodNNYYiTmcv', // Grilo
                'HK8K8eL72mstK1PAMbFhoyCrvqFaA7PHgGpaLrdMow4t', // Grilo
                'CKwX2nkg2bKusk3j6e6yPJGg3TwP7ZzkRvEJ2V4WFL12', // Grilo
                'EX4Dq1jffahb2RjuJ2ZYGjFQj4WAyHDRcY6eAz22AXnn', // Antalm
            ],
            tokens: [],
            version: '-',
            isDark,
            sound,
            connecting: false,
            connected,
            authenticated: false,
            token: null,
            tokensBalance: {
                topaz: 0,
                sapphire: 0,
                emerald: 0,
                ruby: 0,
            },
            ticketsBalance: {
                brass: 0,
                apex: 0,
                burnout: 0,
                torque: 0,
                shakedown: 0,
            },
            account: null,
            accountMenuShow: false,
            bgGameSponsor: null,
            bgLobbySponsor: null,
            modals: {
                name_modal: false,
                pfp_modal: false,
                deposit_modal: false,
                withdraw_modal: false,
                start_game_modal: false,
                swap_tokens_modal: false,
                spin_modal: false,
            },
            activePane: 'games-list',
            admin: {
                show: false,
            },
            gamesList: {
                tab: 'sitAndGo',
                online: true,
                show: true,
                fetching: false,
                list: [],
                private_list: [],
                queue: {},
                private_queue: {}
            },
            gameLobby: {
                show: false,
                game: null,
            },
            globalChat: {
                show: false,
                messages: [],
                new: 0,
                lastSeen: dayjs(),
                lastUpdate: dayjs()
            },
            notifications: {
                show: false,
                messages: [],
                new: 0,
                lastSeen: dayjs(),
                lastUpdate: dayjs(),
                loadMore: false
            },
            yourGamesList: {
                show: false,
                fetching: false,
                showAll: false,
                games: [],
                next_page: 1,
                last_page: 0,
                new: 0,
                lastSeen: dayjs(),
                lastUpdate: dayjs(),
            },
            game: {
                show: false,
                log: false,
                id: null,
                template_id: null,
                turns: 0,
                grid: [],
                players: [],
                messages: [],
                countdown: -1,
                winner: null
            },
            appInfo: {
                show: false
            },
            gameConfig: {
                show: false
            },
            transactions: {
                show: false
            },
            revenue: {
                show: false
            },
            jackpot: {
                current: 0,
                next: 0,
                turns: 0
            },
            users: [],
            pusherState: 'app-init',
            debug: {
                status: false,
                messages: [],
            },
            sprites: {
                files: [],
                list: {}
            },
            sounds: {
                click: null,
                startRaceSmall: null,
                startRaceLong: null,
                bgMusic: null,
                victory: null,
                newRace: null
            },
            wallet: null,
            spin: {
                ready: false,
                prize: '',
            }
        }
    },
    computed: {
        buttons() {
            return {
                'notifications': this.notifications.show,
                'gamesList': this.gamesList.show,
                'gameLobby': this.gameLobby.show,
                'game': this.game.show,
                'globalChat': this.globalChat.show,
                'yourGamesList': this.yourGamesList.show,
                'appInfo': this.appInfo.show,
                'gameConfig': this.gameConfig.show,
            }
        },
        online() {
            return this.users.length
        },
        bgLobby() {
            return this.bgLobbySponsor !== null ? this.bgLobbySponsor : '/images/bg-bear.jpg'
        },
        bgGame() {
            return this.bgGameSponsor
        },
        isTeamWallet() {
            return this.authenticated && this.team.includes(useWallet().publicKey.value.toString())
        },
        auto_start() {
            return this.account?.start_game || false
        }
    },
    async mounted() {
        this.debug.status = process.env.MIX_APP_ENV !== 'production' ? true : false
        this.loadSounds()
        this.toggleSound()
        window.addEventListener('visibilitychange', (e) => { this.visibilityChanged(e) }, false)
        window.addEventListener('offline', (e) => { this.networkStatus('offline') }, false)
        window.addEventListener('online', (e) => { this.networkStatus('online') }, false)
        await this.fetchVersion()
        await this.fetchRrTokens()
        await this.fetchSponsor()
        await this.fetchGamesAvailable()
        await this.fetchLatestMessages()
        await this.fetchJackpot()
        await this.fetchSprites()
        await this.listeners()
    },
    unmounted() {
        window.removeEventListener('visibilitychange', (e) => { this.visibilityChanged(e) }, false)
        window.removeEventListener('offline', (e) => { this.networkStatus('offline') }, false)
        window.removeEventListener('online', (e) => { this.networkStatus('online') }, false)
    },
    watch: {
        async connected(value) {
            if (value) {
                if (this.accountMenuShow === true) {
                    this.accountMenuShow = false
                }
                this.signIn()
            } else {
                console.log('Not Connected!')
                this.reset()
            }
        },
        async disconnecting(value) {
            console.log('dc')
        },
        'gamesList.private_queue'(value) {
            if (Object.keys(value).length == 0 && this.gamesList.tab === 'specials' && !this.isTeamWallet) {
                this.gamesList.tab = 'sitAndGo'
            }
        }
    },
    methods: {
        selectedTab(tab) {
            this.gamesList.tab = tab
        },
        acceptedDisclaimer() {
            this.disclaimer = false
            this.emitter.emit('play-click')
        },
        toggleSound() {
            Howler.mute(!this.sound)
        },
        loadSounds() {
            this.sounds.click = new Howl({
                src: ['/sounds/click.mp3'],
                volume: 0.5
            })
            this.sounds.startRaceSmall = new Howl({
                src: ['/sounds/race_start_small_beep.mp3'],
                volume: 1
            })
            this.sounds.startRaceLong = new Howl({
                src: ['/sounds/race_start_long_beep.mp3'],
                volume: 1
            })
            this.sounds.bgMusic = new Howl({
                src: ['/sounds/race_bg_music.mp3'],
                volume: 0.2,
                loop: true
            })
            this.sounds.victory = new Howl({
                src: ['/sounds/race_victory.mp3'],
                volume: 1
            })
            this.sounds.newRace = new Howl({
                src: ['/sounds/race_new.mp3'],
                volume: 0.85
            })
        },
        networkStatus(type) {
            this.debug.messages.push('Network: ' + type)
            if (type === 'online') {
                this.debug.messages.push('network back online - pusher state: ' + window.LaravelEcho.connector.pusher.connection.state)
                if (window.LaravelEcho.connector.pusher.connection.state !== 'connected') {
                    this.debug.messages.push('Network online and connect')
                    window.LaravelEcho.connector.pusher.connect()
                }
            }
        },
        visibilityChanged(e) {
            if (!window.document.hidden) {
                this.debug.messages.push('Visibility Changed: ' + window.LaravelEcho.connector.pusher.connection.state)
                if (window.LaravelEcho.connector.pusher.connection.state !== 'connected') {
                    this.debug.messages.push('Visibility changed and connect')
                    window.LaravelEcho.connector.pusher.connect()
                }
            }
        },
        async fetchVersion() {
            axios.get('/api/version').then((response) => {
                this.version = response.data.version
            })
        },
        async fetchLatestMessages() {
            axios.get('/api/messages').then((response) => {
                this.globalChat.messages = response.data.messages
            }).catch(() => {
                this.$toast.error('Error fetching latest messages.')
            })
        },
        async fetchLatestNotifications() {
            axios.get('/api/notifications', {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            }).then((response) => {
                this.notifications.messages = response.data.notifications
            }).catch(() => {
                this.$toast.error('Error fetching latest notifications.')
            })
        },
        async fetchGamesAvailable() {
            this.gamesList.fetching = true
            axios.get('/api/games/available').then((response) => {
                this.gamesList.list = response.data.list
                this.gamesList.private_list = response.data.private_list
            }).catch(() => {
                this.$toast.error('Error fetching Available games.')
            }).then(() => {
                this.gamesList.fetching = false
            })
        },
        async fetchPrivateGamesAvailable() {
            axios.get('/api/games/available/private').then((response) => {
                this.gamesList.private_list = response.data.private_list
            }).catch(() => {
                this.$toast.error('Error fetching Special games.')
            })
        },
        async fetchJackpot() {
            axios.get('/api/jackpot').then((response) => {
                this.jackpot.current = response.data.current
                this.jackpot.next = response.data.next
                this.jackpot.turns = response.data.turns
            }).catch(() => {
                this.$toast.error('Error fetching Jackpot.')
            })
        },
        async fetchSponsor() {
            await axios.get('/api/sponsor/' + this.subdomain).then((response) => {
                this.bgGameSponsor = response.data.bgGameSponsor
                this.bgLobbySponsor = response.data.bgLobbySponsor
            })
        },
        async fetchRrTokens() {
            await axios.get('/api/tokens').then((response) => {
                this.tokens = response.data.data
            })
        },
        async fetchSprites() {
            await axios.get('/api/sprites').then(async (response) => {
                this.sprites.files = response.data.list
                await this.preloadAllImages()
            }).catch(() => {
                this.$toast.error('Error fetching Sprites.')
            })
        },
        async preloadAllImages() {
            await this.sprites.files.forEach(async (item) => {
                this.sprites.list[item] = await preloadImage.handle(this.getSprite(item))
            })
        },
        getSprite(sprite) {
            return '/images/sprites/' + sprite + '.svg'
        },
        async updateQueue() {
            await axios.get('/api/games/queue').catch(() => {
                this.gamesList.online = false
            })
        },
        async updateTokenBalance() {
            await axios.post('/api/account/token/balance', {}, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            })
        },
        async updateTicketBalance() {
            await axios.post('/api/account/ticket/balance', {}, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            })
        },
        async getGamesList(page = 1) {
            this.yourGamesList.fetching = true
            const all = this.yourGamesList.showAll
            await axios.post('/api/games?page=' + page, {
                'wallet': useWallet().publicKey.value.toString(),
                all
            }, {
                headers: {
                    'Authorization': `Bearer ${this.token}`
                }
            }).then((res) => {
                if (page == 1) {
                    this.yourGamesList.games = []
                }
                let data = res.data.games.data.map(item => {
                    const user = item.players.map(player => {
                        if (player.user.pfp === null) {
                            player.user.pfp = '/images/bear.jpg'
                        }
                        return player
                    })
                    return item
                })
                this.yourGamesList.games = [...this.yourGamesList.games, ...data]
                this.yourGamesList.last_page = res.data.games.last_page
                this.yourGamesList.next_page = res.data.games.current_page + 1
            }).catch((err) => {
                console.log(err)
            }).then(() => {
                this.yourGamesList.fetching = false
            })
        },
        reset() {
            this.authenticated = false
            this.token = null
            this.account = null
            this.activePane = 'games-list'
            this.transactions.show = false
            this.revenue.show = false
            this.notifications.show = false
            this.gamesList.show = true
            this.gameLobby.show = false
            this.globalChat.show = false
            this.yourGamesList.show = false
            this.appInfo.show = false
            this.gameConfig.show = false
            this.game.show = false
            window.LaravelEcho.leave(`tokens.${this.wallet}`)
            window.LaravelEcho.leave(`account.${this.wallet}`)
            window.LaravelEcho.leave(`games.${this.wallet}`)
            window.LaravelEcho.leave(`notifications.${this.wallet}`)
            window.LaravelEcho.leave('users')
        },
        async signIn() {
            if (!this.connected) {
                return
            }
            this.wallet = useWallet().publicKey.value.toString()
            this.connecting = true
            axios.post('/api/nonce', {
                'wallet': useWallet().publicKey.value.toString()
            }).then(async (response) => {
                const message = new TextEncoder().encode(response.data.message, 'utf-8');
                let signature
                try {
                    signature = await useWallet().signMessage.value(message)
                } catch (err) {
                    this.$toast.error('You canceled the signature request.')
                    return
                }
                await axios.post('/api/login', {
                    'wallet': useWallet().publicKey.value.toString(),
                    'signature': signature
                }).then(async (response) => {
                    this.authenticated = true
                    this.token = response.data.token
                    this.account = response.data.account
                    window.LaravelEcho.connector.options.auth.headers.Authorization = 'Bearer ' + this.token;
                    this.privateListeners()
                    await this.fetchLatestNotifications()
                    await this.getGamesList()
                    await this.updateQueue()
                    this.updateTokenBalance()
                    this.updateTicketBalance()
                    this.disclaimer = !this.isTeamWallet
                }).catch((e) => {
                    console.log(e)
                    this.$toast.error('Login failed.')
                }).finally(() => {
                    this.connecting = false
                })
            }).catch(() => {
                this.$toast.error('Getting nonce failed.')
            }).finally(() => {
                this.connecting = false
            })
        }
    }
}
</script>
